import type { ArenaLossReason } from '../domain/arena-rules';
import { formatPauseReasons } from '../i18n/dynamic-labels';
import { translate, type Locale } from '../i18n/resources';
import type { PauseReason } from '../runtime/pause-controller';

export interface HudState {
  readonly timeRemainingMilliseconds: number;
  readonly health: number;
  readonly maximumHealth: number;
  readonly wave: number;
  readonly totalWaves: number;
  readonly combo: number;
  readonly comboRemainingMilliseconds: number;
  readonly dashRemainingMilliseconds: number;
  readonly score: number;
  readonly result: 'playing' | 'won' | 'lost';
  readonly paused: boolean;
  readonly pauseReasons: readonly PauseReason[];
}

export interface HudSnapshot {
  readonly timer: string;
  readonly timerCue: string;
  readonly health: string;
  readonly healthCue: string;
  readonly wave: string;
  readonly combo: string;
  readonly dash: string;
  readonly score: string;
  readonly result: string;
  readonly pause: string;
}

const formatScore = (score: number, locale: Locale): string =>
  Math.max(0, Math.trunc(score)).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US');

export const createHudSnapshot = (state: HudState, locale: Locale = 'en'): HudSnapshot => {
  const seconds = Math.max(0, Math.ceil(state.timeRemainingMilliseconds / 1_000));
  const minutes = Math.floor(seconds / 60);
  const health = Math.max(0, Math.min(state.maximumHealth, Math.trunc(state.health)));
  const pauseReasons = formatPauseReasons(locale, state.pauseReasons);
  const secondsSuffix = locale === 'ru' ? 'с' : 's';

  return {
    timer: `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`,
    timerCue: translate(
      locale,
      seconds === 0 ? 'hud.expired' : seconds <= 15 ? 'hud.urgent' : 'hud.ready',
    ),
    health: `${'◆'.repeat(health)}${'◇'.repeat(state.maximumHealth - health)} ${health}/${state.maximumHealth}`,
    healthCue:
      health === 0
        ? translate(locale, 'hud.depleted')
        : health === 1
          ? translate(locale, 'hud.critical')
          : health < state.maximumHealth
            ? translate(locale, 'hud.damaged')
            : translate(locale, 'hud.stable'),
    wave: translate(locale, 'hud.waveValue', { current: state.wave, total: state.totalWaves }),
    combo: `×${Math.max(1, state.combo)} · ${(Math.max(0, state.comboRemainingMilliseconds) / 1_000).toFixed(1)}${secondsSuffix}`,
    dash:
      state.dashRemainingMilliseconds <= 0
        ? translate(locale, 'hud.dashReady')
        : `${(state.dashRemainingMilliseconds / 1_000).toFixed(1)}${secondsSuffix}`,
    score: formatScore(state.score, locale),
    result:
      state.result === 'won'
        ? translate(locale, 'hud.missionComplete')
        : state.result === 'lost'
          ? translate(locale, 'hud.runEnded')
          : translate(locale, 'hud.inProgress'),
    pause: state.paused
      ? translate(locale, 'hud.paused', { reasons: pauseReasons.join(' + ') })
      : translate(locale, 'hud.running'),
  };
};

export const resultAnnouncement = (
  outcome: 'won' | 'lost',
  score: number,
  lossReason?: ArenaLossReason,
  locale: Locale = 'en',
): string => {
  if (outcome === 'won') {
    return translate(locale, 'announcement.win', {
      score: formatScore(score, locale),
    });
  }
  const cause = translate(
    locale,
    lossReason === 'time-cap' ? 'announcement.timerExpired' : 'announcement.healthDepleted',
  );
  return translate(locale, 'announcement.loss', {
    cause,
    score: formatScore(score, locale),
  });
};

export const renderHud = (root: ParentNode, snapshot: HudSnapshot): void => {
  for (const [field, value] of Object.entries(snapshot)) {
    const target = root.querySelector<HTMLElement>(`[data-hud="${field}"]`);
    if (target !== null && target.textContent !== value) target.textContent = value;
  }

  const hud = root.querySelector<HTMLElement>('#game-hud');
  if (hud !== null) hud.hidden = false;
};

export const hideHud = (root: ParentNode): void => {
  const hud = root.querySelector<HTMLElement>('#game-hud');
  if (hud !== null) hud.hidden = true;
};

export const announceStatus = (root: ParentNode, message: string): void => {
  const status = root.querySelector<HTMLElement>('#game-status');
  if (status !== null && status.textContent !== message) status.textContent = message;
};

export const announceCaption = (
  root: ParentNode,
  message: string,
  enabled: boolean,
): void => {
  const caption = root.querySelector<HTMLElement>('#sound-caption');
  const next = enabled ? message : '';
  if (caption !== null && caption.textContent !== next) caption.textContent = next;
};
